import { CANCEL } from 'redux-saga';
import { call } from 'redux-saga/effects';
import type { FetchQueryOptions, QueryKey, WithRequired } from '@tanstack/react-query';
import { queryClient } from '@/services/query/queryClient';

/**
 * Reads a query from a saga: `const capabilities: CompanyCapabilities = yield fetchQueryEffect(companyCapabilitiesQuery(companyId))`.
 *
 * When the saga is cancelled, the query is cancelled and reverted, so a late answer never reaches the cache —
 * unless a component observes the query: then it completes and the component gets the data. A cancelled route
 * saga does not mean the page was left: the navigator cancels and re-forks route sagas on every location change.
 * Cancelling does not abort the HTTP request unless the query function passes TanStack's `signal` to it.
 *
 * An effect, not a function for `call`: `call` erases this function's generics and the options stop type-checking.
 */
export const fetchQueryEffect = <TQueryFnData, TError, TData, TQueryKey extends QueryKey>(
  options: WithRequired<FetchQueryOptions<TQueryFnData, TError, TData, TQueryKey>, 'queryKey'>,
) =>
  call(() => {
    const cancel = () => {
      const query = queryClient.getQueryCache().find({ queryKey: options.queryKey, exact: true });
      if (query && query.getObserversCount() > 0) return;
      void queryClient.cancelQueries({ queryKey: options.queryKey, exact: true });
    };
    return Object.assign(queryClient.fetchQuery(options), { [CANCEL]: cancel });
  });
